import { useRef } from 'react';
import { Editor } from 'ckeditor5';

const INDEXED_STICKY_TOP_OFFSET = 0;
const INDEXED_STICKY_Z_INDEX = '100';
const INDEXED_STICKY_CLASS = 'sticky-toolbar-stuck';

export class ToolbarManager {
  private indexedStickyTeardown: (() => void) | undefined;

  getToolbarParentElementWithIndex = (
    pageHeaderSectionIndex: string,
  ): HTMLElement => {
    const toolbarParentElement = document.getElementById(
      pageHeaderSectionIndex,
    );
    if (toolbarParentElement) {
      return toolbarParentElement;
    }
    throw new Error('could not locate editor toolbar target');
  };

  setToolbarToParentFromEditor = (
    editor: Editor,
    pageHeaderSectionIndex: string,
  ): void => {
    const toolbarElement = editor?.ui?.view?.toolbar?.element ?? null;
    const toolbarParentElement = this.getToolbarParentElementWithIndex(
      pageHeaderSectionIndex,
    );
    if (toolbarElement) {
      toolbarParentElement.insertBefore(toolbarElement, null);
      this.setupIndexedStickyToolbar(toolbarParentElement);
    }
  };

  /*
   *
   * Modeled after document-editor's ToolbarWrapper.tsx
   * Adds a sticky toolbar that adjust via JS rather than being stuck to the top of the window
   * Done this way due to overflow-x in Qualio Frontend preventing sticky elements from actually sticking to the window
   */
  private setupIndexedStickyToolbar = (headerElement: HTMLElement): void => {
    this.indexedStickyTeardown?.();

    const sectionBoundary = headerElement.parentElement;
    if (!sectionBoundary) {
      return;
    }

    const placeholder = document.createElement('div');
    placeholder.style.display = 'none';
    placeholder.style.width = '100%';
    sectionBoundary.insertBefore(placeholder, headerElement);

    let stuck = false;

    const measureHeader = () => {
      placeholder.style.height = `${headerElement.offsetHeight}px`;
    };
    const headerObserver = new ResizeObserver(measureHeader);
    headerObserver.observe(headerElement);
    measureHeader();

    const syncHorizontalBox = () => {
      if (!stuck) {
        return;
      }
      const { left, width } = placeholder.getBoundingClientRect();
      if (width === 0) {
        return;
      }
      headerElement.style.left = `${left}px`;
      headerElement.style.width = `${width}px`;
    };
    const placeholderObserver = new ResizeObserver(syncHorizontalBox);
    placeholderObserver.observe(placeholder);

    const engage = () => {
      if (stuck) {
        return;
      }
      stuck = true;
      placeholder.style.display = 'block';
      headerElement.classList.add(INDEXED_STICKY_CLASS);
      headerElement.style.position = 'fixed';
      headerElement.style.top = `${INDEXED_STICKY_TOP_OFFSET}px`;
      headerElement.style.zIndex = INDEXED_STICKY_Z_INDEX;
      syncHorizontalBox();
    };

    const disengage = () => {
      if (!stuck) {
        return;
      }
      stuck = false;
      headerElement.classList.remove(INDEXED_STICKY_CLASS);
      headerElement.style.position = '';
      headerElement.style.top = '';
      headerElement.style.left = '';
      headerElement.style.width = '';
      headerElement.style.zIndex = '';
      placeholder.style.display = 'none';
    };

    const updatePosition = () => {
      if (!sectionBoundary.isConnected) {
        teardown();
        return;
      }
      const referenceTop = stuck
        ? placeholder.getBoundingClientRect().top
        : headerElement.getBoundingClientRect().top;
      const boundaryRect = sectionBoundary.getBoundingClientRect();
      const shouldStick =
        referenceTop <= INDEXED_STICKY_TOP_OFFSET &&
        boundaryRect.bottom >
          INDEXED_STICKY_TOP_OFFSET + headerElement.offsetHeight;

      if (shouldStick) {
        engage();
        syncHorizontalBox();
      } else {
        disengage();
      }
    };

    const teardown = () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      headerObserver.disconnect();
      placeholderObserver.disconnect();
      disengage();
      placeholder.remove();
    };

    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);
    updatePosition();

    this.indexedStickyTeardown = teardown;
  };

  teardownIndexedStickyToolbar = (): void => {
    this.indexedStickyTeardown?.();
    this.indexedStickyTeardown = undefined;
  };
}

export const useToolbarManager = (): ToolbarManager => {
  const toolbarManagerRef = useRef<ToolbarManager>();
  if (!toolbarManagerRef.current) {
    toolbarManagerRef.current = new ToolbarManager();
  }
  return toolbarManagerRef.current;
};
